class Api {
  constructor(baseUrl, token = null) {
    this.baseUrl = baseUrl || process.env.REACT_APP_API_BASE_URL;
    this.token = token;
    this.refreshTimer = null;
    this.isRefreshing = false;
  }

  setToken(token) {
    this.token = token;
    this.scheduleTokenRefresh(token);
  }

  // Programa la renovación automática del token
  scheduleTokenRefresh(token) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }
    
    if (!token) return;

    try {
      // Decodifica el token JWT para obtener la fecha de expiración
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expTime = payload.exp * 1000; // exp está en segundos, necesitamos milisegundos
      const currentTime = Date.now();
      
      // Programa refresh 5 minutos antes de la expiración
      const refreshTime = expTime - currentTime - (5 * 60 * 1000);
      
      if (refreshTime > 0) {
        this.refreshTimer = setTimeout(() => {
          this.proactiveRefresh();
        }, refreshTime);
      }
    } catch (e) {
      console.warn('No se pudo programar refresh automático:', e);
    }
  }

  // Refresh proactivo (antes de que expire)
  async proactiveRefresh() {
    if (this.isRefreshing) return;
    
    try {
      this.isRefreshing = true;
      const newToken = await this.refreshToken();
      console.log('Token renovado automáticamente');
      return newToken;
    } catch (error) {
      console.warn('Error en refresh automático:', error);
      // Si falla, programa un nuevo intento en 1 minuto
      setTimeout(() => this.proactiveRefresh(), 60000);
    } finally {
      this.isRefreshing = false;
    }
  }

  getHeaders(extra = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...extra,
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async refreshToken() {
    const res = await fetch(this.baseUrl + '/auth/refresh', {
      method: 'POST',
      headers: this.getHeaders(),
    });
    if (!res.ok) throw new Error('Cannot refresh token');
    const data = await res.json();
    if (data.token) {
      const oldToken = this.token;
      this.setToken(data.token);
      localStorage.setItem('token', data.token);
      
      // Notifica al contexto de la aplicación sobre el nuevo token
      window.dispatchEvent(new CustomEvent('token-refreshed', { 
        detail: { newToken: data.token, oldToken } 
      }));
      
      return data.token;
    }
    throw new Error('No token in refresh response');
  }

  async fetchWithRefresh(fetchFn, ...args) {
    try {
      return await fetchFn.apply(this, args);
    } catch (err) {
      if (err.message === 'Session expired') {
        try {
          await this.refreshToken();
          // Reintenta la petición original con el nuevo token
          return await fetchFn.apply(this, args);
        } catch (refreshErr) {
          // Si falla el refresh, dispara el evento de sesión expirada
          window.dispatchEvent(new Event('session-expired'));
          throw refreshErr;
        }
      }
      throw err;
    }
  }

  async get(path, params = {}) {
    const url = new URL(this.baseUrl + path);
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    const res = await fetch(url, {
      headers: this.getHeaders(),
    });
    if (res.status === 401) {
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async post(path, data = {}) {
    const res = await fetch(this.baseUrl + path, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (res.status === 401) {
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async put(path, data = {}) {
    const res = await fetch(this.baseUrl + path, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data),
    });
    if (res.status === 401) {
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }

  async delete(path) {
    const res = await fetch(this.baseUrl + path, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    if (res.status === 401) {
      throw new Error('Session expired');
    }
    if (!res.ok) throw new Error(await res.text());
    // Si es 204 No Content, no intentes parsear JSON
    if (res.status === 204) {
      return { success: true };
    }
    return res.json();
  }

  // --- API helpers ---
  async getRecipes(params = {}) {
    return this.fetchWithRefresh(this.get, '/recipes', params);
  }
  async getRecipe(id) {
    return this.fetchWithRefresh(this.get, `/recipes/${id}`);
  }
  async postRecipe(data) {
    return this.fetchWithRefresh(this.post, '/recipes', data);
  }
  async putRecipe(id, data) {
    return this.fetchWithRefresh(this.put, `/recipes/${id}`, data);
  }
  async deleteRecipe(id) {
    return this.fetchWithRefresh(this.delete, `/recipes/${id}`);
  }
  async getIngredients(params = {}) {
    return this.fetchWithRefresh(this.get, '/ingredients', params);
  }
  async getIngredient(id, params = {}) {
    return this.fetchWithRefresh(this.get, `/ingredients/${id}`, params);
  }
  async postIngredient(data) {
    return this.fetchWithRefresh(this.post, '/ingredients', data);
  }
  async putIngredient(id, data) {
    return this.fetchWithRefresh(this.put, `/ingredients/${id}`, data);
  }
  async deleteIngredient(id) {
    return this.fetchWithRefresh(this.delete, `/ingredients/${id}`);
  }
  async getCategories() {
    return this.fetchWithRefresh(this.get, '/categories');
  }

  // AI API methods
  async getAIKeysStatus() {
    return this.fetchWithRefresh(this.get, '/user/ai-keys-status');
  }

  async updateAIKeys(data) {
    return this.fetchWithRefresh(this.post, '/user/ai-keys', data);
  }

  async removeAIKey(provider) {
    return this.fetchWithRefresh(this.delete, '/user/ai-keys', { provider });
  }

  async testAIKey(provider, apiKey) {
    return this.fetchWithRefresh(this.post, '/user/test-ai-key', {
      provider,
      api_key: apiKey
    });
  }

  async generateIngredient(description, preferredProvider = 'gemini') {
    return this.fetchWithRefresh(this.post, '/ai/generate-ingredient', {
      description,
      preferred_provider: preferredProvider
    });
  }

  async updateUserLanguage(language) {
    return this.fetchWithRefresh(this.put, '/user/language', { language });
  }

  // Recipe translations
  async getRecipeTranslations(recipeId) {
    return this.fetchWithRefresh(this.get, `/recipes/${recipeId}/translations`);
  }

  async addRecipeTranslation(recipeId, lang, name, notes) {
    return this.fetchWithRefresh(this.post, `/recipes/${recipeId}/translations`, {
      lang,
      name,
      notes
    });
  }

  async updateRecipeTranslation(recipeId, lang, name, notes) {
    return this.fetchWithRefresh(this.put, `/recipes/${recipeId}/translations/${lang}`, {
      name,
      notes
    });
  }

  async deleteRecipeTranslation(recipeId, lang) {
    return this.fetchWithRefresh(this.delete, `/recipes/${recipeId}/translations/${lang}`);
  }
}

export default Api;
